import { useCallback } from 'react';

import { HealthcareFacilityUnit } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { FormContextValues, useFormController, useTranslate } from '@dreryk/react-common';
import { AutoLayout } from '@dreryk/react-components';

import { StyledFormSectionHeader } from '../../../../../common/StyledFormSectionHeader';
import { healthcareOrganizationTranslationKeys } from '../../../../../translations';
import {
   createHealthcareAddressFormBean,
   HealthcareAddressFormBean,
   HealthcareAddressFormPart,
} from '../../../../common/HealthcareAddressFormPart';
import { HealthcareBasicData } from '../../../../common/HealthcareBasicData';
import {
   createHealthcareContactFormBean,
   HealthcareContactFormBean,
   HealthcareContactFormPart,
} from '../../../../common/HealthcareContactFormPart';
import { HealthcareIdFormController } from '../../../../common/HealthcareIdFormController';
import { HealthcareProviderNameInput } from '../../../../common/HealthcareProviderNameInput';
import { ScopeOfProvidedServicesInteractiveTable } from '../../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable';
import { ScopeOfProvidedServicesFormBeanPart } from '../../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable.types';
import { mapScopeOfProvidedServicesToFormBeanPart } from '../../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable.utils';

import { UnitCodeInput } from './fields/UnitCodeInput';

export type HealthcareFacilityUnitUpdateFormBean = {
   id: number;
   name: string;
   unitCode: string | null;
} & HealthcareAddressFormBean &
   HealthcareContactFormBean &
   ScopeOfProvidedServicesFormBeanPart;

export type HealthcareFacilityUnitUpdateFormController = FormContextValues<HealthcareFacilityUnitUpdateFormBean>;

const getInitialValues = (healthcareFacilityUnit: HealthcareFacilityUnit): HealthcareFacilityUnitUpdateFormBean => {
   return {
      id: healthcareFacilityUnit.id,
      name: healthcareFacilityUnit.name,
      unitCode: healthcareFacilityUnit.unitCode ?? null,
      ...mapScopeOfProvidedServicesToFormBeanPart(healthcareFacilityUnit.scopeOfProvidedServicesList),
      ...createHealthcareAddressFormBean(
         healthcareFacilityUnit.usingHealthcareFacilityAddressData,
         healthcareFacilityUnit.address
      ),
      ...createHealthcareContactFormBean(
         healthcareFacilityUnit.usingHealthcareFacilityContactData,
         healthcareFacilityUnit
      ),
   };
};

type HealthcareFacilityUnitUpdateFormRenderProps = Pick<
   HealthcareFacilityUnitUpdateFormController,
   'handleSubmit' | 'formState'
> & {
   setUnitCodeNotUniqueErrorMessage: VoidFunction;
};

type HealthcareFacilityUnitUpdateFormProps = {
   readonly healthcareFacilityUnit: HealthcareFacilityUnit;
   readonly healthcareParentData: HealthcareBasicData;
   readonly children: (renderProps: HealthcareFacilityUnitUpdateFormRenderProps) => JSX.Element;
};

// eslint-disable-next-line max-lines-per-function
export const HealthcareFacilityUnitUpdateForm = (props: HealthcareFacilityUnitUpdateFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      formState,
      watch,
      setError,
   } = useFormController<HealthcareFacilityUnitUpdateFormBean>(getInitialValues(props.healthcareFacilityUnit));
   const translate = useTranslate();

   const { usingParentAddressData, usingParentContactData } = watch([
      'usingParentAddressData',
      'usingParentContactData',
   ]);

   const setUnitCodeNotUniqueErrorMessage = useCallback(() => {
      setError('unitCode', { message: healthcareOrganizationTranslationKeys.codeMustBeUniqueWithinOrganization });
   }, [setError]);

   return (
      <AutoLayout as={'div'} $direction={'vertical'} $spacing={12} $horizontalResizing={'fill'}>
         <StyledFormSectionHeader>
            {translate(healthcareOrganizationTranslationKeys.healthcareFacilityUnitData)}
         </StyledFormSectionHeader>

         <AutoLayout as={'form'} noValidate $direction={'vertical'} $horizontalResizing={'fill'} $spacing={36}>
            <HealthcareIdFormController name={'id'} register={register} required />
            <AutoLayout
               as={HealthcareProviderNameInput}
               name={'name'}
               register={register}
               required
               $horizontalResizing={'fill'}
            />

            <HealthcareAddressFormPart
               register={register}
               usingParentAddressData={usingParentAddressData}
               usingParentAddressDataLabel={healthcareOrganizationTranslationKeys.usingHealthcareFacilityAddressData}
               healthcareParentData={props.healthcareParentData}
            />

            <HealthcareContactFormPart
               register={register}
               usingParentContactData={usingParentContactData}
               usingParentContactDataLabel={healthcareOrganizationTranslationKeys.usingHealthcareFacilityContactData}
               healthcareParentData={props.healthcareParentData}
            />

            <AutoLayout as={'div'} $direction={'horizontal'} $horizontalResizing={357}>
               <UnitCodeInput name={'unitCode'} register={register} required />
            </AutoLayout>

            <AutoLayout as={ScopeOfProvidedServicesInteractiveTable} register={register} $horizontalResizing={'fill'} />

            {props.children({
               handleSubmit: handleSubmit,
               formState: formState,
               setUnitCodeNotUniqueErrorMessage: setUnitCodeNotUniqueErrorMessage,
            })}
         </AutoLayout>
      </AutoLayout>
   );
};
